import { useTranslation } from "react-i18next";
import MapWithLayers from "../map/MapWithLayer";
import "./TransportOrderDetailModal.css";
import { Button, Divider, Space } from "antd";
import ProgressStep from "../progressSteps/ProgressSteps";

function TransportOrderDetailView({ orderDetail, routeLayer, publicMode, onEdit, onGenerateLink }) {
  const { t } = useTranslation();

  if (!orderDetail) return <p>{t("orderDetail.noData")}</p>;

  const formatOrderStatus = (status) => {
    if (!status) return "N/A";
    const key = status.toLowerCase();
    return t(`transportManagement.${key}`, {
      defaultValue: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    });
  };

  const formatOrderType = (type) => {
    if (!type) return "N/A";
    if (type === "CONTAINER_OUTBOUND") {
      return t("orderType.containerOutbound", { defaultValue: "Container Export" });
    }
    if (type === "CONTAINER_INBOUND") {
      return t("orderType.containerInbound", { defaultValue: "Container Import" });
    }
    if (type === "CONVENTIONAL_PICKUP") {
      return t("orderType.conventionalPickup", { defaultValue: "Conventional Pickup" });
    }
    if (type === "CONVENTIONAL_DELIVERY") {
      return t("orderType.conventionalDelivery", { defaultValue: "Conventional Delivery" });
    }
    return type;
  };

  const latitude = parseFloat(orderDetail?.checkPoints?.[0]?.latitude) || 0;
  const longitude = parseFloat(orderDetail?.checkPoints?.[0]?.longitude) || 0;

  const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp._seconds !== undefined) {
      return new Date(timestamp._seconds * 1000).toLocaleString();
    }
    return "N/A";
  };

  const buildTruckflowPointRows = () => {
    const points = orderDetail?.truckflow?.points || {};
    const rows = [];
    const type = orderDetail?.type || "";

    if (type === "CONTAINER_INBOUND") {
      if (points.origin) rows.push({ label: t("mapEditor.pickupFullContainer"), point: points.origin });
      if (points.customer) rows.push({ label: t("mapEditor.deliveryGoodsLocation"), point: points.customer });
      if (points.gateIn) rows.push({ label: t("mapEditor.returnEmptyContainer"), point: points.gateIn });
    } else if (type === "CONTAINER_OUTBOUND") {
      if (points.origin) rows.push({ label: t("mapEditor.pickupEmptyContainer"), point: points.origin });
      if (points.customer) rows.push({ label: t("mapEditor.loadingGoodsLocation"), point: points.customer });
      if (points.gateIn) rows.push({ label: t("mapEditor.returnFullContainer"), point: points.gateIn });
    } else if (type === "CONVENTIONAL_PICKUP" || type === "CONVENTIONAL_DELIVERY") {
      const originPoint = points.origin || points.pickup;
      const customerPoint = points.customer || points.delivery;
      if (originPoint) rows.push({ label: t("mapEditor.pickUp"), point: originPoint });
      if (customerPoint) rows.push({ label: t("mapEditor.delivery"), point: customerPoint });
    }

    if (!rows.length && points) {
      Object.entries(points).forEach(([key, point]) => {
        if (!point) return;
        rows.push({ label: key, point });
      });
    }

    return rows;
  };

  const truckflowPointRows = buildTruckflowPointRows();
  const showTruckflow = ["ACTIVE", "FINALIZED"].includes(String(orderDetail?.status || "").toUpperCase());
  const orderHasActiveDriver = ["ASSIGNED", "ACTIVE"].includes(
    String(orderDetail?.status || "").toUpperCase()
  );

  return (
    <div className="public-order-page">
      <div
        className="public-header"
        style={{
          backgroundColor: "var(--chakra-colors-gandolapp-500)",
        }}
      >
        <div className="content-wrapper">
          <h1 className="header-data">
            <strong>{t("orderDetail.internalReference")}:</strong>{" "}
            <span className="header-data">{orderDetail.internalReference || orderDetail.transportOrderId}</span>
          </h1>
          <p className="header-data">
            <strong>{t("orderDetail.customerReference")}:</strong>{" "}
            <span className="header-data">{orderDetail.customerReference || orderDetail.transportOrderRef || "N/A"}</span>
          </p>

          <p className="header-data">
            <strong>{t("orderDetail.consignee")}:</strong>{" "}
            <span className="header-data">{orderDetail.transfollow?.consignee?.name || "N/A"}</span>
          </p>

          <p className="header-data">
            <strong>{t("directory.role.CUSTOMER")}:</strong>{" "}
            <span className="header-data">{orderDetail.customer?.name || "N/A"}</span>
          </p>

          <p className="header-data">
            <strong>{t("directory.role.CONSIGNOR")}:</strong>{" "}
            <span className="header-data">{orderDetail.transfollow?.consignor?.name || "N/A"}</span>
          </p>

          <p className="header-data">
            <strong>{t("orderDetail.goods")}:</strong>{" "}
            <span className="header-data">
              {orderDetail.transfollow?.structuredGoods?.map((g) => g.name).join(", ") || "N/A"}
            </span>
          </p>
        </div>
      </div>

      <div className="public-content">
        <div className="content-wrapper">
          <div className="modal-container">
            <div className="modal-container main">
              <section className="modal-container data">
                <div className="modal-detail">
                  <span className="detail-label">{t("orderDetail.status")}:</span>{" "}
                  {formatOrderStatus(orderDetail.status)}
                </div>
                <div className="modal-detail">
                  <span className="detail-label">{t("orderDetail.type")}:</span>{" "}
                  {formatOrderType(orderDetail.type)}
                </div>
                <div className="modal-detail">
                  <span className="detail-label">{t("orderDetail.placeOfDelivery")}:</span>{" "}
                  {orderDetail.transfollow?.placeOfDelivery?.name || "N/A"}
                </div>
                <div className="modal-detail">
                  <span className="detail-label">{t("orderDetail.placeOfTakingOver")}:</span>{" "}
                  {orderDetail.transfollow?.placeOfTakingOver?.name || "N/A"}
                </div>
              </section>

              {!publicMode && (
                <div className="modal-container buttons">
                  <Space wrap>
                    <Button onClick={onEdit}>{t("orderDetail.edit")}</Button>
                    <Button type="primary" onClick={onGenerateLink}>
                      {t("orderDetail.newLink")}
                    </Button>
                  </Space>
                </div>
              )}
            </div>

            <div
              className="detail-container"
              style={{
                paddingBottom: "20px",
              }}
            >
              <section className="date-detail">
                <div className="modal-detail">
                  <span className="detail-label">{t("orderDetail.start")}:</span>{" "}
                  {formatFirestoreTimestamp(orderDetail.activationAt)}
                </div>
              </section>

              <section className="truck-detail">
                <div>
                  <div className="modal-detail">
                    <span className="detail-label">{t("orderDetail.truckId")}:</span>{" "}
                    {orderDetail.truck?.headId || "N/A"}
                  </div>
                  <div className="modal-detail">
                    <span className="detail-label">{t("orderDetail.trailerId")}:</span>{" "}
                    {orderDetail.truck?.trailerId || "N/A"}
                  </div>
                </div>
              </section>
              {showTruckflow && truckflowPointRows.length > 0 && (
                <section className="truck-detail">
                  <div>
                    <div className="modal-detail">
                      <span className="detail-label">
                        {t("orderDetail.route", { defaultValue: "Truckflow points" })}:
                      </span>
                    </div>
                    {truckflowPointRows.map(({ label, point }, idx) => (
                      <div key={`${label}-${idx}`} className="modal-detail">
                        <span className="detail-label">{label}:</span>{" "}
                        {point?.name || point?.address || `${point?.latitude}, ${point?.longitude}`}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>

          <Divider className="modal-divider"></Divider>
          {showTruckflow && (
            <div className="modal-checklist">
              <div className="modal-divider" />
              <ProgressStep
                type={orderDetail.type}
                records={
                  orderDetail.truckflow?.transitions ||
                  orderDetail.truckflow?.transitionRecords ||
                  orderDetail.shipmentData?.transitionRecords ||
                  []
                }
                anomalies={
                  orderDetail.truckflow?.anomalies ||
                  orderDetail.truckflow?.transitionAnomalies ||
                  orderDetail.shipmentData?.transitionAnomalies ||
                  []
                }
                currentState={orderDetail.truckflow?.state || orderDetail.shipmentData?.state}
              />
            </div>
          )}

          {latitude && longitude && (
            <div className="modal-imageGallery">
              <div className="modal-divider" />
              <MapWithLayers
                latitude={latitude}
                longitude={longitude}
                layers={[
                  { id: "route", type: "polyline", data: routeLayer || [] },
                  routeLayer?.length
                    ? {
                        id: "driver",
                        type: "marker",
                        markerKind: "driver",
                        data: [
                          {
                            latitude: routeLayer[routeLayer.length - 1].latitude,
                            longitude: routeLayer[routeLayer.length - 1].longitude,
                            isOnline: false,
                            hasActiveOrder: orderHasActiveDriver,
                          },
                        ],
                      }
                    : null,
                ].filter(Boolean)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransportOrderDetailView;
